<template>
  <div class="business-intelligence-executive">
    <!-- 📊 Header Business Intelligence -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-3" color="purple" size="28">mdi-brain</v-icon>
        <div>
          <h2 class="text-h4 font-weight-bold">Business Intelligence Ejecutivo</h2>
          <!-- P13-V7B (refutation): the subtitle no longer promises "Recomendaciones IA" — see the
               retired panel below. -->
          <p class="text-subtitle-1 mb-0 text-grey">Métricas ejecutivas dentro de su alcance</p>
        </div>
        <!-- P13-V7B (refutation): the green "Sistema Operativo" chip is RETIRED. It was a literal in
             the template — no health check behind it — and it stayed green above the error alert
             when the dashboard read failed. -->
      </v-card-title>
    </v-card>

    <!--
      P13-V5A-R1: an ERROR is now rendered as an error. Previously a failed load silently produced a
      healthy-looking executive dashboard from an invented fallback dataset.
    -->
    <v-alert
      v-if="executiveState === 'error'"
      type="error"
      variant="tonal"
      class="mb-6"
      data-testid="executive-dashboard-error"
    >
      No se pudieron obtener los datos del dashboard ejecutivo.
      <span v-if="executiveError"> ({{ executiveError }})</span>
      No se muestran métricas sustitutas.
    </v-alert>

    <!--
      P13-V5A-R1 (Repair E): the backend did not declare the R1 truth contract, so this build refuses
      to render its numbers. This is what allows the frontend to merge FIRST safely.
    -->
    <v-alert
      v-if="executiveState === 'incompatible'"
      type="warning"
      variant="tonal"
      class="mb-6"
      data-testid="executive-dashboard-incompatible"
    >
      El servidor no declara el contrato de veracidad <strong>P13-V5A-R1</strong>.
      No se muestran métricas de negocio hasta que el backend correspondiente esté desplegado.
    </v-alert>

    <!-- 📈 Métricas Ejecutivas — valores reales dentro del alcance autorizado -->
    <v-row class="mb-6" v-if="executiveState !== 'error' && executiveState !== 'incompatible'">
      <v-col cols="12" md="3" v-for="(metric, index) in executiveMetrics" :key="index">
        <v-card :color="metric.color" dark elevation="4" class="text-center">
          <v-card-text>
            <v-icon size="40" class="mb-2">{{ metric.icon }}</v-icon>
            <!--
              The previous card also rendered a hardcoded trend (2.3 / 1.2 / -0.8 / 4.2) with an
              up/down arrow. There is no trend producer, so no trend is shown.
            -->
            <div class="text-h3 font-weight-bold" data-testid="metric-value">{{ metric.value }}</div>
            <div class="text-body-1">{{ metric.label }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Alcance realmente aplicado por el servidor, visible para el usuario. -->
    <v-alert
      v-if="appliedScope"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-6"
      data-testid="applied-scope"
    >
      Alcance aplicado: {{ appliedScope.mode }}
      <span v-if="appliedScope.administration_ids">
        · administraciones: {{ appliedScope.administration_ids.join(', ') }}
      </span>
    </v-alert>

    <!--
      P13-V7B (refutation): the "Recomendaciones IA Ejecutivas" panel is RETIRED. Its only producer,
      GET /dashboard/ai-recommendations, answers 501 by design (ExecutiveDashboardController: no
      canonical producer owned by R1), and `aiUnavailableReason` — the one honest sentence the
      previous repair wrote — was never rendered: the panel said "No hay recomendaciones disponibles",
      as if a recommender had run and found nothing, beside an "Actualizar" that re-asked for the same
      501 and an "Implementar" posting to a writer that does not exist.
    -->
    <v-row class="mb-6">
      <!-- 📊 Panel de Performance -->
      <v-col cols="12" lg="4">
        <v-card elevation="3">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2" color="blue">mdi-chart-line</v-icon>
            Performance Dashboard
          </v-card-title>
          <v-card-text>
            <!--
              P13-V5A-R1: these three gauges (Eficiencia / SLA / Uptime) were driven by
              `|| 87`, `|| 94`, `|| 99.9` fallbacks and, on failure, by an invented dataset.
              None has a canonical producer owned by R1, so no number is rendered at all — an empty
              gauge with an explicit label rather than a plausible percentage.
            -->
            <div class="mb-4" data-testid="performance-not-available">
              <p class="text-subtitle-2 mb-2">Rendimiento, SLA y Disponibilidad</p>
              <v-alert type="info" variant="tonal" density="compact" class="mb-0">
                Sin datos: estas métricas no tienen productor canónico en esta versión.
              </v-alert>
            </div>

            <v-divider class="my-4"></v-divider>

            <div class="text-center">
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 📈 Gráficos Avanzados -->
    <v-row>
      <v-col cols="12" lg="6">
        <v-card elevation="3">
          <v-card-title>
            <v-icon class="mr-2" color="indigo">mdi-chart-areaspline</v-icon>
            Tendencias Operativas
          </v-card-title>
          <v-card-text>
            <div class="text-center py-8 text-grey">
              <v-icon size="64" color="grey-lighten-2">mdi-chart-line-variant</v-icon>
              <p class="mt-3">Gráfico de tendencias en desarrollo</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" lg="6">
        <v-card elevation="3">
          <v-card-title>
            <v-icon class="mr-2" color="teal">mdi-chart-donut</v-icon>
            Distribución de Recursos
          </v-card-title>
          <v-card-text>
            <div class="text-center py-8 text-grey">
              <v-icon size="64" color="grey-lighten-2">mdi-chart-donut-variant</v-icon>
              <p class="mt-3">Distribución de recursos en desarrollo</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { ApiClient } from '@/services/api-client'

/**
 * P13-V5A-R1 — CONSUMER TRUTH.
 *
 * Before R1 this component fabricated twice over:
 *   1. every KPI card used a FALSY fallback (`… || '97%'`, `|| '94%'`, `|| '6'`, `|| '157/150'`), and
 *      because the backend never produced those field names the fallback fired even on SUCCESS;
 *   2. the `catch` substituted an invented object ("Fallback data para desarrollo") so a backend
 *      failure rendered as a healthy executive dashboard.
 *
 * A falsy fallback is also wrong for a legitimate zero: `0 || '6'` is `'6'`. Real zero, no-data and
 * error are now three distinct rendered states and no business value is ever invented.
 */
/**
 * P13-V5A-R1 REPAIR (Repair E) — TRUTH-CONTRACT GATE, which is what makes FRONTEND_FIRST a SAFE
 * merge order.
 *
 * A pre-R1 backend still answers `/dashboard/executive` 200 with a `system_overview` object — but
 * that object contains the fabricated block (`total_incidents: 47`, `active_incidents: 12`,
 * `completion_rate: 85.2`) and carries no canonical scope. Rendering it would make this repaired
 * component display the old mock as if it were truth.
 *
 * So the component REQUIRES the marker the repaired backend emits and the legacy one cannot. Without
 * it the component fails closed: explicit incompatible state, and NO business fact displayed.
 */
const R1_TRUTH_CONTRACT = 'P13-V5A-R1'

type LoadState = 'idle' | 'loading' | 'ok' | 'error' | 'incompatible'

const loadingExecutive = ref(false)
const executiveState = ref<LoadState>('idle')
const executiveError = ref<string | null>(null)
const overview = ref<any>(null)
const appliedScope = ref<any>(null)
// P13-V7B (refutation): the AI-recommendation state (`loadingAI`, `aiRecommendations`,
// `aiUnavailableReason`) is RETIRED with its panel — see the template.

/** Explicit no-data marker rendered instead of a plausible number. */
const NO_DATA_LABEL = 'Sin datos'

/**
 * Render a metric honestly: a real 0 stays 0; NO_DATA renders as an explicit label; a missing metric
 * never falls back to a business-looking value.
 */
function renderMetric(key: string, suffix = ''): string {
  const data = overview.value
  if (!data) return NO_DATA_LABEL
  const state = data.metric_states?.[key]
  if (state === 'NO_DATA' || state === 'NOT_AVAILABLE') return NO_DATA_LABEL
  const value = data[key]
  if (value === null || value === undefined) return NO_DATA_LABEL
  return `${value}${suffix}`
}

// 📊 Métricas ejecutivas — cada una respaldada por un agregado real dentro del alcance autorizado.
const executiveMetrics = computed(() => [
  {
    label: 'Incidencias Totales',
    value: renderMetric('total_incidents'),
    icon: 'mdi-clipboard-list',
    color: 'blue'
  },
  {
    label: 'Incidencias Activas',
    value: renderMetric('active_incidents'),
    icon: 'mdi-progress-clock',
    color: 'info'
  },
  {
    label: 'Incidencias Críticas',
    value: renderMetric('critical_incidents'),
    icon: 'mdi-alert-circle',
    color: 'warning'
  },
  {
    label: 'Tiempo Medio de Resolución',
    value: renderMetric('avg_resolution_hours', ' h'),
    icon: 'mdi-timer-outline',
    color: 'success'
  }
])

// 🎯 Performance: sin productor canónico en R1; no se inventa ningún número.
const performanceData = computed(() => ({
  efficiency: null,
  sla: null,
  uptime: null
}))

/**
 * Locate the dashboard payload regardless of which transport shape the caller receives.
 *
 * Three shapes coexist in this repository and reach this component depending on which `ApiClient`
 * the import resolves to:
 *   - `services/api-client.ts` (what `@/services/api-client` resolves to) returns the response BODY,
 *     already unwrapped from `{ success, data }` by the canonical response interceptor;
 *   - `services/api/api-client.ts` re-wraps that body as `{ data, status }`;
 *   - a bare axios response carries the untouched `{ success, data }` envelope.
 *
 * Reading `.data` unconditionally therefore yields `undefined` on the first shape, which made the
 * truth gate below reject a perfectly valid R1 payload and render the incompatible state against the
 * REPAIRED backend. This selects the object that actually carries the dashboard shape, and returns
 * null when none does — so an unrecognised shape still FAILS CLOSED rather than rendering anything.
 */
function extractDashboardPayload(response: any): any | null {
  const candidates = [response, response?.data, response?.data?.data]
  return (
    candidates.find(
      (candidate) =>
        candidate && typeof candidate === 'object' && 'meta' in candidate && 'system_overview' in candidate
    ) ?? null
  )
}

// 🔄 Cargar Dashboard Ejecutivo
async function loadExecutiveDashboard() {
  loadingExecutive.value = true
  executiveState.value = 'loading'
  executiveError.value = null
  try {
    const dashboardResponse = await ApiClient.get('/dashboard/executive')
    const payload = extractDashboardPayload(dashboardResponse)

    // FAIL CLOSED on a payload that does not carry the R1 truth guarantees. Both conditions matter:
    // the explicit marker, and the granular metric-state map a legacy response never produces.
    const contract = payload?.meta?.truth_contract
    const hasMetricStates = Boolean(payload?.system_overview?.metric_states)
    if (contract !== R1_TRUTH_CONTRACT || !hasMetricStates) {
      overview.value = null
      appliedScope.value = null
      executiveState.value = 'incompatible'
      executiveError.value =
        'La respuesta del servidor no declara el contrato de veracidad P13-V5A-R1.'
      return
    }

    overview.value = payload.system_overview
    appliedScope.value = payload.scope ?? null
    executiveState.value = 'ok'
  } catch (error: any) {
    // A failure REMAINS a failure. No fallback dataset, no plausible KPI.
    console.error('❌ Error cargando BI Dashboard:', error)
    overview.value = null
    appliedScope.value = null
    executiveState.value = 'error'
    executiveError.value =
      error?.response?.data?.error || error?.message || 'No se pudo cargar el dashboard ejecutivo.'
  } finally {
    loadingExecutive.value = false
  }
}

// P13-V7B (refutation): `loadAIRecommendations` and `implementRecommendation` are RETIRED. The first
// read a 501 on every mount (GET /dashboard/ai-recommendations has no producer); the second posted to
// `/dashboard/ai-recommendations/implement`, which no backend route defines. (P13-V5A-R1 had already
// removed the three invented recommendations its `catch` used to render.)

// 🔄 Inicialización
onMounted(async () => {
  console.log('🚀 Business Intelligence Ejecutivo montado')
  await loadExecutiveDashboard()
})
</script>

<style scoped>
.business-intelligence-executive {
  padding: 20px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.v-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}

.v-timeline-item {
  margin-bottom: 16px;
}

.v-progress-linear {
  border-radius: 4px;
}
</style>
